import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Input, Layout, Tooltip } from 'antd';
import {
  FullscreenExitOutlined,
  FullscreenOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  UserOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import useAirPlusLmsLanguage from 'src/hooks/useAirPlusLmsLanguage';

import CampusModal from '../Modal/CampusModal';
import FullScreenButton from 'src/components/common/FullScreenButton';
import OpenAiModal from '../Modal/OpenAiModal';
import StudentModal from '../Modal/StudentModal';
import TeacherModal from 'src/components/Modal/TeacherModal';
import { isLogedIn, userInfoVar, userVar } from 'src/apollo/cache';
import { useApolloClient } from '@apollo/client';
import { GET_STUDENT_LUPUSER, GET_TEACHER_USER } from 'src/operations/queries/getUser';
import { USER_ME } from 'src/operations/queries/getUser';
import { useQueryProxy } from 'src/operations/proxy/user';
import { getCurrentToken } from '../../utils';
import { storage, useModal } from 'src/utils/hooks';
import * as queries from 'src/operations/queries';

const { Header: HeaderWrapper } = Layout;

const HeaderContainer = styled(HeaderWrapper)`
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  height: 64px;
  min-height: 64px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #ffffff;
  border-bottom: 1.5px solid rgba(8, 145, 178, 0.14);
  box-shadow: 0 2px 16px rgba(8, 145, 178, 0.1);
  line-height: 1;

  @media (max-width: 1024px) {
    padding: 0 16px;
    gap: 10px;
  }
  ${({ $isDashboard }) => $isDashboard && `
    @media (max-width: 600px) {
      padding: 0 10px;
      gap: 8px;
      .dashboard-lms, .dashboard-user-info { display: none; }
      > div { gap: 6px; }
      img { max-width: 96px; height: auto; }
      button { flex-shrink: 0; }
      .dashboard-user { padding: 4px; }
    }
  `}
  ${({ $isStudentDashboard }) => $isStudentDashboard && `
    @media (max-width: 900px) {
      .dashboard-menu { display: none; }
      @media (max-width: 768px), (max-width: 1024px) and (max-height: 600px) {
        .dashboard-menu { display: inline-flex; }
      }
    }
  `}

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    height: 52px; min-height: 52px; padding: 0 8px; gap: 4px;
    && > div { gap: 4px; }
    && img { max-width: 58px; height: 36px; }
    && .dashboard-user { padding: 3px; }
    @media (max-width: 360px) {
      padding: 0 6px;
      && img { max-width: 44px; height: 30px; }
    }
  }
`;

const LeftSection = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 768px), (max-width: 1024px) and (max-height: 600px) { gap: 6px; }

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    gap: 4px; flex-shrink: 0;
  }
`;

const RightSection = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  @media (max-width: 768px), (max-width: 1024px) and (max-height: 600px) { gap: 6px; }

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    gap: 4px; flex-shrink: 0;
  }
`;

const RoundIconButton = styled.button`
  width: 40px;
  height: 40px;
  border: 1.5px solid rgba(8, 145, 178, 0.16);
  border-radius: 12px;
  background: ${(props) => props.background || '#ffffff'};
  color: ${(props) => props.color || '#4a6380'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.18s ease;

  flex-shrink: 0;
  @media (max-width: 768px), (max-width: 1024px) and (max-height: 600px) {
    width: 36px;
    height: 36px;
  }

  &:hover {
    border-color: #0891b2;
    color: #0891b2;
    background: rgba(8, 145, 178, 0.08);
  }

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    width: 32px; height: 34px; border-radius: 10px; font-size: 15px;
  }
`;

const CloseEmojiButton = styled(RoundIconButton)`
  font-size: 18px;
  color: #ef4444;

  &:hover {
    border-color: #ef4444;
    color: #ef4444;
    background: rgba(239, 68, 68, 0.08);
  }
`;

const LogoGroup = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

const LogoImage = styled.img`
  display: block;
  width: auto;
  height: 60px;
  object-fit: contain;

  @media (max-width: 1024px) {
    height: 56px;
  }
  @media (max-width: 768px), (max-width: 1024px) and (max-height: 600px) { height: 42px; }
`;

const SearchBox = styled.div`
  flex: 1;
  max-width: 360px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0f9ff;
  border: 1.5px solid rgba(8, 145, 178, 0.16);
  border-radius: 999px;
  padding: 0 16px;
  height: 40px;
  transition: all 0.2s;

  &:focus-within {
    border-color: #0891b2;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
  }

  input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    font-family: 'Nunito', Arial, 'Pretendard Variable', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #0c1a2e;

    &::placeholder {
      color: #8fa3bb;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserBadge = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 14px 4px 6px;
  border-radius: 999px;
  border: 1.5px solid rgba(8, 145, 178, 0.16);
  background: #ffffff;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: #0891b2;
    box-shadow: 0 2px 10px rgba(8, 145, 178, 0.12);
  }

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    padding: 3px; gap: 0; flex-shrink: 0;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0891b2, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    width: 28px; height: 28px; font-size: 13px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
  min-width: 0;
  @media (max-width: 420px) { display: none; }
  @media (max-width: 768px), (max-width: 1024px) and (max-height: 600px) {
    max-width: 76px;
    .name { width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }

  .name {
    font-size: 13px;
    font-weight: 800;
    color: #0c1a2e;
  }
  .role {
    font-size: 11px;
    font-weight: 600;
    color: #0891b2;
  }

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    display: none;
  }
`;

const LMSButton = styled.button`
  padding: 8px 16px;
  border-radius: 12px;
  border: 1.5px solid #0891b2;
  background: #0891b2;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  @media (max-width: 768px), (max-width: 1024px) and (max-height: 600px) {
    font-size: 0;
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    gap: 0;
    .anticon { font-size: 16px; }
  }

  &:hover {
    background: #0e7490;
    border-color: #0e7490;
    box-shadow: 0 2px 8px rgba(8, 145, 178, 0.25);
  }

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    width: 32px; height: 34px; border-radius: 10px;
  }
`;

const LangToggle = styled.div`
  flex-shrink: 0;
  white-space: nowrap;
  display: flex;
  align-items: center;
  border: 1.5px solid rgba(8, 145, 178, 0.2);
  border-radius: 999px;
  overflow: hidden;
  background: #ffffff;
  padding: 2px;
  box-shadow: 0 1px 4px rgba(8, 145, 178, 0.08);
`;

const LangButton = styled.button`
  white-space: nowrap;
  border: 0;
  border-radius: 999px;
  background: ${(props) => (props.active ? 'linear-gradient(135deg, #0891b2, #06b6d4)' : 'transparent')};
  color: ${(props) => (props.active ? '#ffffff' : '#334155')};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  padding: 6px 14px;
  cursor: pointer;
  transition: background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
  line-height: 1;
  @media (max-width: 768px) { padding: 6px; font-size: 11px; }

  &:hover {
    color: ${(props) => (props.active ? '#ffffff' : '#0891b2')};
  }

  @media screen and (max-width: 768px), screen and (max-width: 1024px) and (max-height: 600px) {
    padding: 7px 6px; font-size: 11px;
  }
`;

function AirPlusHeader({ collapsed, onCollapse, closeHeader = false, hideMenu = false, hideSearch = false,  isDashboard = false }) {

  const { t: airT } = useAirPlusLmsLanguage();

  const history = useHistory();
  const client = useApolloClient();
  const onLogout = () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      isLogedIn(false);
      userInfoVar(null);
      client.clearStore();
      client.cache.gc();
      storage.removeItem('culp_token');
      window.location.reload();
    }
  };
  const location = useLocation();
  const token = getCurrentToken();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { language: lang, setLanguage: handleLanguageSwitch } = useAirPlusLmsLanguage();

  const { visible, showModal, handleCancel } = useModal();

  const { data: userData } = useQueryProxy(USER_ME, {
    variables: { token },
    skip: !token,
  });

  const currentUser = userData?.me;
  const userType = useMemo(() => {
    if (userInfoVar()) {
      return userVar()[userInfoVar().type];
    }
    return '';
  }, []);
  const isTeacherOrAdmin = [1, 2, 3].includes(Number(currentUser?.type));

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      history.push(`/air-plus/search?q=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  const handleExit = () => {
    history.push('/');
  };

  const handleClose = () => {
    if (window.Android?.closeApp) {
      window.Android.closeApp();
      return;
    }
    history.goBack();
  };

  const showCloseButton = closeHeader || Boolean(window.Android?.closeApp);

  return (
    <HeaderContainer $isDashboard={isDashboard} $isStudentDashboard={isDashboard && Number(currentUser?.type) === 0}>
      <LeftSection>
        {!hideMenu && (
          <RoundIconButton className="dashboard-menu" onClick={onCollapse} aria-label={airT("Toggle Menu")}>
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </RoundIconButton>
        )}

        <LogoGroup onClick={() => history.push('/air-plus')}>
          <LogoImage src="/images/AirPlus/logo.png" alt="aiR Library" />
        </LogoGroup>

        {!hideSearch && (
          <SearchBox>
            <SearchOutlined style={{ color: '#0891b2', fontSize: 16 }} />
            <input
              placeholder={lang === 'ko' ? airT("도서 또는 키워드 검색") : airT("Search books or keywords")}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleSearch}
            />
          </SearchBox>
        )}
      </LeftSection>

      <RightSection>
        {isTeacherOrAdmin && (
          <LMSButton className="dashboard-lms" onClick={() => history.push('/air-plus/manage/booklist')}>
            <SettingOutlined />{airT(" LMS 관리 ")}</LMSButton>
        )}

        <Tooltip title={isFullscreen ? airT("Exit Fullscreen") : airT("Fullscreen")}>
          <RoundIconButton aria-label={airT("Toggle fullscreen")} onClick={toggleFullscreen}>{isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}</RoundIconButton>
        </Tooltip>

          <LangToggle aria-label="AirPlus language">
            <LangButton type="button" aria-pressed={lang === 'en'} active={lang === 'en'} onClick={() => handleLanguageSwitch('en')}>
              EN
            </LangButton>
            <LangButton type="button" aria-pressed={lang === 'ko'} active={lang === 'ko'} onClick={() => handleLanguageSwitch('ko')}>한국어</LangButton>
          </LangToggle>

        {currentUser && (
          <UserBadge className="dashboard-user" onClick={() => showModal('edit')}>
            <UserAvatar>{currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : <UserOutlined />}</UserAvatar>
            <UserInfo className="dashboard-user-info">
              <span className="name">{currentUser?.name || currentUser?.id}</span>
              <span className="role">{currentUser?.type === 0 ? airT("Student") : airT("Teacher")}</span>
            </UserInfo>
          </UserBadge>
        )}

        <Tooltip title={isDashboard ? airT("Logout") : airT("Exit")}>
          <RoundIconButton type="button" onClick={isDashboard ? onLogout : handleExit} aria-label={isDashboard ? airT("로그아웃") : 'exit'}>
            {'\u274C'}
          </RoundIconButton>
        </Tooltip>

        {showCloseButton && (
          <Tooltip title={airT("Close")}>
            <CloseEmojiButton type="button" onClick={handleClose} aria-label="close">
              {'\u274C'}
            </CloseEmojiButton>
          </Tooltip>
        )}
      </RightSection>

      {userType === 'fc' && (
        <TeacherModal userInfo={currentUser} popupMode="edit" handleCancel={handleCancel} visible={visible} myInfo={true} />
      )}
      {userType === 'campus' && (
        <CampusModal
          campusInfo={currentUser?.campus}
          popupMode="edit"
          handleCancel={handleCancel}
          visible={visible}
          campus_idx={currentUser?.campus?.idx}
        />
      )}
      {userType === 'manager' && (
        <TeacherModal userInfo={currentUser} popupMode="edit" handleCancel={handleCancel} visible={visible} myInfo={true} />
      )}
      {userType === 'student' && (
        <StudentModal userInfo={currentUser} popupMode="edit" handleCancel={handleCancel} visible={visible} myInfo={true} />
      )}
    </HeaderContainer>
  );
}

export default AirPlusHeader;
